import {
  type ReviewEvidence,
  type ReviewFindingRow,
  type ReviewScoutRow,
  type ReviewStage,
  type ReviewStreamRow,
} from "@mg/schemas/src/christo/askPuntt";
import {
  ArrowsMerge,
  CaretDown,
  CheckCircle,
  Circle,
  CircleNotch,
  MinusCircle,
  Warning,
  XCircle,
} from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import cx from "classnames";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  STATUS_PANEL_ITEMS_MUTED_CLASS,
  STATUS_PANEL_TOGGLE_MUTED_CLASS,
} from "./AgentSteps";
import { formatElapsed } from "./ReviewChecklist";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";

/**
 * Live artwork-review progress surfaced while a v2 review runs: the backend
 * drives the ordered stage rows (Prepare · Read · Review · Verify · Report) via
 * `chat.reviewStages`, and an evidence counter ("N / M verified") via
 * `chat.reviewEvidence`. Unlike the legacy ReviewChecklist (client-side timer),
 * every state here reflects real pipeline progress. Renders nothing until the
 * backend has written at least one stage.
 *
 * Shares the collapsible status-panel shell with SearchProgress / ReviewChecklist
 * / ThinkingState via the STATUS_PANEL_* constants, so it reads as one surface
 * with its neighbours in the thread rather than a bare list.
 */

/** Name of the stage the live scout/finding stream is rendered under. Mirrors
 * `_STAGE_ORDER` in the rembrandt activity that writes the bar. */
const REVIEW_STAGE_NAME = "review";

const ROW_CLASS =
  "flex w-full items-center gap-2 rounded-lg border border-puntt-neutral-gray-4 bg-base-white px-[11px] py-[7px]";

/** Testid namespacer. The bar mounts twice on a chat with the artwork pane
 * open (in-thread + in-pane), so every id needs a per-instance suffix or
 * Playwright's strict mode resolves two nodes for one selector. */
type Tid = (name: string) => string;

function StatusIcon({ status }: { status: ReviewScoutRow["status"] }) {
  if (status === "done") {
    return (
      <CheckCircle
        size={14}
        weight="fill"
        className="shrink-0 text-malachite-700"
        aria-hidden
      />
    );
  }
  if (status === "running") {
    return (
      <CircleNotch
        size={14}
        className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
        aria-hidden
      />
    );
  }
  // A crashed or timed-out producer used to render the same hollow circle as
  // one that had not started, so a review that lost a scout looked identical to
  // one still queuing it. Labelled rather than aria-hidden: unlike the other
  // states, this one is not repeated in the row's text.
  if (status === "failed" || status === "timed_out") {
    return (
      <XCircle
        size={14}
        weight="fill"
        className="shrink-0 text-puntt-red-9"
        aria-label={status === "failed" ? "failed" : "timed out"}
      />
    );
  }
  if (status === "skipped") {
    return (
      <MinusCircle
        size={14}
        className="shrink-0 text-puntt-neutral-gray-8"
        aria-label="skipped"
      />
    );
  }
  return (
    <Circle
      size={14}
      className="shrink-0 text-puntt-neutral-gray-8"
      aria-hidden
    />
  );
}

function ScoutRow({ scout, tid }: { scout: ReviewScoutRow; tid: Tid }) {
  const { t } = useTranslation("ui");
  const ran = scout.status === "done" || scout.status === "running";
  return (
    <div
      data-testid={tid(`review-progress-scout-${scout.id}`)}
      className={ROW_CLASS}
      title={
        scout.status === "done"
          ? undefined
          : t("reviewProgress.scoutStatusTitle", { status: scout.status })
      }
    >
      <StatusIcon status={scout.status} />
      <Text
        size="1"
        weight="medium"
        className={cx(
          "flex-1",
          ran ? "text-puntt-neutral-gray-12" : "text-puntt-neutral-gray-9",
        )}
      >
        {scout.label}
      </Text>
      {scout.draftCount > 0 && (
        <Text size="1" className="shrink-0 text-puntt-neutral-gray-9">
          {t("reviewProgress.draftCount", { count: scout.draftCount })}
        </Text>
      )}
    </div>
  );
}

const FINDING_STATE_LABEL_KEY: Record<ReviewFindingRow["state"], string> = {
  draft: "reviewProgress.findingState.drafted",
  emitted: "reviewProgress.findingState.queued",
  verifying: "reviewProgress.findingState.verifying",
  accepted: "reviewProgress.findingState.accepted",
  rejected: "reviewProgress.findingState.rejected",
  dismissed: "reviewProgress.findingState.dismissed",
  unresolved: "reviewProgress.findingState.unresolved",
  merged: "reviewProgress.findingState.merged",
};

function FindingStateChip({
  finding,
  tid,
}: {
  finding: ReviewFindingRow;
  tid: Tid;
}) {
  const { t } = useTranslation("ui");
  const { state } = finding;
  const label = t(FINDING_STATE_LABEL_KEY[state]);
  return (
    <Text
      size="1"
      data-testid={tid(`review-progress-finding-state-${finding.id}`)}
      aria-label={t("reviewProgress.findingStateAriaLabel", {
        title: finding.title,
        label,
      })}
      className={cx(
        "flex shrink-0 items-center gap-1",
        state === "accepted"
          ? "text-malachite-700"
          : state === "rejected"
            ? "text-puntt-red-9"
            : "text-puntt-neutral-gray-9",
      )}
    >
      {state === "accepted" && (
        <CheckCircle size={12} weight="fill" aria-hidden />
      )}
      {state === "rejected" && <XCircle size={12} weight="fill" aria-hidden />}
      {state === "verifying" && (
        <CircleNotch
          size={12}
          className="animate-spin motion-reduce:animate-none"
          aria-hidden
        />
      )}
      {state === "merged" && <ArrowsMerge size={12} aria-hidden />}
      {label}
    </Text>
  );
}

function FindingRow({ finding, tid }: { finding: ReviewFindingRow; tid: Tid }) {
  const { t } = useTranslation("ui");
  // Per-row state: a lifted map would collapse the siblings whenever one row
  // opens, and the whole point is reading one finding while the rest stream in.
  const [expanded, setExpanded] = useState(false);
  const meta = [
    finding.area,
    finding.page != null
      ? t("reviewProgress.metaPage", { page: finding.page })
      : null,
    finding.confidence != null
      ? t("reviewProgress.metaConfidence", {
          confidence: finding.confidence.toFixed(2),
        })
      : null,
  ].filter(Boolean);

  return (
    <div
      data-testid={tid(`review-progress-finding-${finding.id}`)}
      className="pl-4"
    >
      <button
        type="button"
        data-testid={tid(`review-progress-finding-toggle-${finding.id}`)}
        aria-expanded={expanded}
        aria-label={t("reviewProgress.findingToggleAriaLabel", {
          action: expanded
            ? t("reviewProgress.collapse")
            : t("reviewProgress.expand"),
          title: finding.title,
        })}
        onClick={() => setExpanded(!expanded)}
        className={cx(ROW_CLASS, "text-left")}
      >
        <CaretDown
          size={12}
          aria-hidden
          className={cx(
            "shrink-0 text-puntt-neutral-gray-9 transition-transform duration-200",
            expanded ? "rotate-0" : "-rotate-90",
          )}
        />
        <Text size="1" className="flex-1 text-puntt-neutral-gray-12">
          {finding.title}
        </Text>
        <FindingStateChip finding={finding} tid={tid} />
      </button>
      {expanded && (
        <div
          data-testid={tid(`review-progress-finding-detail-${finding.id}`)}
          className="flex flex-col gap-0.5 px-[11px] py-1"
        >
          {meta.length > 0 && (
            <Text size="1" className="text-puntt-neutral-gray-11">
              {meta.join(" · ")}
            </Text>
          )}
          {finding.description && (
            <Text size="1" className="text-puntt-neutral-gray-11">
              {finding.description}
            </Text>
          )}
          {finding.note && (
            <Text size="1" className="text-puntt-neutral-gray-9">
              {finding.note}
            </Text>
          )}
        </div>
      )}
    </div>
  );
}

function StreamRow({ row, tid }: { row: ReviewStreamRow; tid: Tid }) {
  return row.kind === "scout" ? (
    <ScoutRow scout={row} tid={tid} />
  ) : (
    <FindingRow finding={row} tid={tid} />
  );
}

function StageRow({ stage, tid }: { stage: ReviewStage; tid: Tid }) {
  const { t } = useTranslation("ui");
  const isDone = stage.status === "done";
  const isActive = stage.status === "active";
  const detail = stage.detail ?? [];
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  // A stage with nothing to report must not get a toggle that opens onto
  // nothing — Prepare never carries numbers.
  const expandable = detail.length > 0;
  const expanded = expandable && (manualToggle ?? isActive);
  const key = stage.name.toLowerCase();

  const row = (
    <>
      <StatusIcon status={isDone ? "done" : isActive ? "running" : "pending"} />
      <Text
        size="1"
        weight="medium"
        className={cx(
          "flex-1",
          isDone || isActive
            ? "text-puntt-neutral-gray-12"
            : "text-puntt-neutral-gray-9",
        )}
      >
        {stage.name}
      </Text>
      {expandable && (
        <CaretDown
          size={12}
          aria-hidden
          className={cx(
            "shrink-0 text-puntt-neutral-gray-9 transition-transform duration-200",
            expanded ? "rotate-0" : "-rotate-90",
          )}
        />
      )}
    </>
  );

  return (
    <div data-testid={tid(`review-progress-stage-${key}`)}>
      {expandable ? (
        <button
          type="button"
          data-testid={tid(`review-progress-stage-toggle-${key}`)}
          aria-expanded={expanded}
          aria-label={t("reviewProgress.stageToggleAriaLabel", {
            action: expanded
              ? t("reviewProgress.collapse")
              : t("reviewProgress.expand"),
            stageName: stage.name,
          })}
          onClick={() => setManualToggle(!expanded)}
          className={cx(ROW_CLASS, "text-left")}
        >
          {row}
        </button>
      ) : (
        <div className={ROW_CLASS}>{row}</div>
      )}
      {expanded && (
        <div
          data-testid={tid(`review-progress-stage-detail-${key}`)}
          className="flex flex-col gap-0.5 px-[11px] py-1"
        >
          {detail.map((line) => (
            <Text key={line} size="1" className="text-puntt-neutral-gray-11">
              {line}
            </Text>
          ))}
        </div>
      )}
    </div>
  );
}

export function ReviewProgressBar({
  stages,
  evidence,
  stream,
  complete = false,
  instanceId = "",
  startedAt,
  warnings,
}: {
  stages?: ReviewStage[];
  evidence?: ReviewEvidence;
  /** What the finished review could NOT do — an outlined pack whose OCR never
   * landed disables every wording check, and without saying so the result reads
   * as a clean pack. */
  warnings?: string[];
  /** Live scout/finding rows, rendered right after the "Review" stage so the
   * user watches the run happen instead of staring at one opaque stage. */
  stream?: ReviewStreamRow[];
  /** When the review has finished, show every stage done regardless of the last
   * stage the backend wrote (the pipeline stops writing after "Report"). */
  complete?: boolean;
  /** Suffix for every testid. The bar mounts twice when the artwork pane is
   * open, and two nodes per selector break Playwright's strict mode. */
  instanceId?: string;
  /** ISO start of the review turn — drives the elapsed clock. A stage can hold
   * for many minutes; a moving number is what says the run is alive. */
  startedAt?: string | null;
}) {
  const { t } = useTranslation("ui");
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);

  // Ticks only while the review is genuinely in flight. Computed before the
  // early return below so the hook order never changes.
  const allStagesDone =
    !!stages && stages.length > 0 && stages.every((s) => s.status === "done");
  const ticking = !!startedAt && !complete && !allStagesDone;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!ticking) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [ticking]);

  if (!stages || stages.length === 0) return null;

  const tid: Tid = (name) => (instanceId ? `${name}-${instanceId}` : name);

  // Every stage done is the backend saying the review is over: the activity
  // emits one terminal event after the comments are saved. Trust that over any
  // client-side guess — `complete` is the caller's older, v1-shaped signal and
  // stays supported.
  const finished = complete || stages.every((s) => s.status === "done");

  const displayStages: ReviewStage[] = finished
    ? stages.map((s) => ({ ...s, status: "done", color: "green" }))
    : stages;

  // Nothing may keep spinning once the review is over: the pipeline stops
  // writing after "Report", so an in-flight row would never resolve. A real
  // verdict is preserved — a rejected finding must not read as accepted.
  const displayStream: ReviewStreamRow[] = finished
    ? (stream ?? []).map((row) =>
        row.kind === "finding" &&
        (row.state === "verifying" || row.state === "emitted")
          ? { ...row, state: "unresolved" }
          : row,
      )
    : (stream ?? []);

  const activeStage = displayStages.find((s) => s.status === "active");
  const isActive = !finished && !!activeStage;
  // Stays open once the review is done. Collapsing on completion hid the whole
  // record — every stage, scout, pass and verdict — at exactly the moment the
  // user turned to read it, and the transcript is the only place it survives.
  // An explicit toggle still wins in both directions.
  const expanded = manualToggle !== null ? manualToggle : isActive || finished;

  const showEvidence = !!evidence && evidence.total > 0 && !finished;
  const summaryText = finished
    ? t("reviewProgress.complete")
    : activeStage
      ? t("reviewProgress.reviewingStage", { stageName: activeStage.name })
      : t("reviewProgress.preparing");
  const elapsedLabel = finished ? null : formatElapsed(startedAt, now);

  return (
    <div className="my-3">
      <div
        data-testid={tid("review-progress-bar")}
        role="status"
        aria-live="polite"
        aria-label={t("reviewProgress.ariaLabel")}
        className={cx(
          "overflow-hidden",
          interactiveSurfaceClasses({ expandable: true, muted: true }),
        )}
      >
        <button
          data-testid={tid("review-progress-toggle")}
          type="button"
          aria-expanded={expanded}
          aria-label={t("reviewProgress.barToggleAriaLabel", {
            action: expanded
              ? t("reviewProgress.collapse")
              : t("reviewProgress.expand"),
          })}
          onClick={() => setManualToggle(!expanded)}
          className={STATUS_PANEL_TOGGLE_MUTED_CLASS}
        >
          {isActive ? (
            <CircleNotch
              size={16}
              className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
              aria-hidden
            />
          ) : (
            <CheckCircle
              size={16}
              weight="fill"
              className="shrink-0 text-malachite-700"
              aria-hidden
            />
          )}
          <Text size="1" weight="medium" className="text-puntt-neutral-gray-12">
            {summaryText}
          </Text>
          {elapsedLabel && (
            <Text
              size="1"
              data-testid={tid("review-progress-elapsed")}
              className="shrink-0 whitespace-nowrap text-puntt-neutral-gray-9"
            >
              {elapsedLabel}
            </Text>
          )}
          {showEvidence && (
            <Text
              size="1"
              data-testid={tid("review-progress-evidence")}
              aria-label={t("reviewProgress.evidenceAriaLabel", {
                verified: evidence.verified,
                total: evidence.total,
              })}
              className="ml-auto text-puntt-neutral-gray-9"
            >
              {t("reviewProgress.evidenceText", {
                verified: evidence.verified,
                total: evidence.total,
              })}
            </Text>
          )}
          <CaretDown
            size={14}
            aria-hidden
            className={cx(
              "shrink-0 text-puntt-neutral-gray-9 transition-transform duration-200",
              showEvidence ? "ml-2" : "ml-auto",
              expanded ? "rotate-0" : "-rotate-90",
            )}
          />
        </button>

        {expanded && (
          <div
            data-testid={tid("review-progress-items")}
            // The rows re-render on every 3s poll and can run to dozens; inside
            // the panel's aria-live="polite" a screen reader would read the
            // whole list out again each time. The summary above stays live.
            aria-live="off"
            className={STATUS_PANEL_ITEMS_MUTED_CLASS}
          >
            <div className="flex flex-col gap-1.5">
              {displayStages.map((stage) => (
                <Fragment key={stage.name}>
                  <StageRow stage={stage} tid={tid} />
                  {stage.name.toLowerCase() === REVIEW_STAGE_NAME &&
                    displayStream.map((row) => (
                      <StreamRow
                        key={`${row.kind}-${row.id}`}
                        row={row}
                        tid={tid}
                      />
                    ))}
                </Fragment>
              ))}
              {/* No stage named "Review" (an older bar shape): the stream still
                  belongs on screen, so it lands at the end rather than vanishing. */}
              {!displayStages.some(
                (stage) => stage.name.toLowerCase() === REVIEW_STAGE_NAME,
              ) &&
                displayStream.map((row) => (
                  <StreamRow
                    key={`${row.kind}-${row.id}`}
                    row={row}
                    tid={tid}
                  />
                ))}
              {!!warnings?.length && (
                <div
                  data-testid={tid("review-progress-warnings")}
                  className="mt-1 flex flex-col gap-1 border-t border-puntt-neutral-gray-5 pt-2"
                >
                  {warnings.map((warning) => (
                    <div key={warning} className="flex items-start gap-1.5">
                      <Warning
                        size={14}
                        weight="fill"
                        className="text-puntt-orange-9 mt-0.5 shrink-0"
                        aria-hidden
                      />
                      <Text size="1" className="text-puntt-neutral-gray-11">
                        {warning}
                      </Text>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
